import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import React, { useEffect, useMemo, useRef } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import { LoaderDialog } from '../../components/LoaderDialog/LoaderDialog';
import { NegotiationStateOverrideContextProvider } from '../../components/NegotiationTestOverride/NegotiationTestOverrideContext';
import PortalMenu from '../../components/PortalMenu/PortalMenu';
import PrivateRoute from '../../components/PrivateRoute/PrivateRoute';
import { config } from '../../config';
import { useBankContext, useBrandContext } from '../../dictionaries/context/BankContext';
import { BankType, DebtManager, ManagedBank } from '../../generated/graphql';
import { session } from '../../utils/auth';
import eventsTracking from '../../utils/eventsTracking';
import { isLocalStorageAvailable } from '../../utils/storage';
import { useGetProfileAndDebtsReport } from '../../utils/useGetProfileAndDebtsReport';
import IdentificationConfirmation from '../IdentificationConfirmation/IdentificationConfirmation';
import IdentityConfirmationDeepLink from '../IdentityConfirmationDeepLink/IdentityConfirmationDeepLink';
import DebtDetails from '../NegotiationDebts/DebtDetails';
import { DebtNegExtraContextProvider } from '../NegotiationDebts/DebtNegExtraContext';
import { NonPaymentReasonFormContextProvider } from '../NegotiationDebts/NonPaymentReasonFormContext';
import SelectDebt from '../NegotiationDebts/SelectDebt';
import PortalNoOutDebts from '../PortalNoAuth/PortalNoOutDebts';
import { portalRootUrl } from './portalRootUrl';

interface IPortalRootProps {}
const idVerificationPath = 'validacion-identidad';
const deepLinkPath = 'verificacion';

const PortalRoot: React.FC<IPortalRootProps> = () => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { path, url } = useRouteMatch();
  portalRootUrl(url);

  const inIdVerification = !!location.pathname.match(new RegExp(idVerificationPath))
    || !!location.pathname.match(new RegExp(deepLinkPath));

  const {
    hasToken, outDebtsReportData, outDebtsReportLoading, profileData, profileLoading,
  } = useGetProfileAndDebtsReport();

  const loading = (profileLoading || outDebtsReportLoading) && hasToken;
  const {
    hasSome: hasOutDebts,
  } = outDebtsReportData?.outDebtsReport || {};

  const deepLinkToken = useMemo(() => {
    const search = new URLSearchParams(location.search);
    const token = search.get('token');
    return token;
  }, [ location ]);

  const banksReport: ManagedBank[] = useMemo(() => {
    return outDebtsReportData?.outDebtsReport.managedBanks || [];
  }, [ outDebtsReportData?.outDebtsReport.managedBanks ]);

  // hotjar identify
  React.useEffect(() => {
    const { document, user } = profileData?.profile || {};
    if (!document || !user) return;
    window.setTimeout(() => {
      const hotjarScript: any = (window as any).hj;
      if (hotjarScript) hotjarScript('identify', user.id, { document });
    }, 1000); // some failsafe time for hotjar script load
  }, [ profileData ]);

  const [ bank, setSelectedBank ] = useBankContext();
  const [ brand, setBrandSelected ] = useBrandContext();

  const brandUpdated = useRef(false);
  useEffect(() => {
    if (!profileData || loading || brandUpdated.current) return;
    if (banksReport.length) {
      brandUpdated.current = true;
      const bankValue = bank?.toUpperCase() === 'DAVIPLATA' ? BankType.BancoDavivienda : bank;
      const bankMatch = banksReport.find(
        (report) => report.brand === brand && report.bank === bankValue,
      );

      // if we don't have any debt that matches with current brand and bank,
      // we set the first one's brand and bank
      if (!bankMatch) {
        // Also, we prioritize any debt with the same brand as asked at the beginning
        // to prevent start with other brand when we have debts from different brands
        const sameBrand = banksReport.filter((report) => report.brand === brand);
        const [ firstBank ] = [ ...sameBrand, ...banksReport ];
        setSelectedBank(firstBank.bank);
        setBrandSelected(firstBank.brand);
      }
    }
  }, [
    history, path, loading, profileData, banksReport,
    bank, brand, setSelectedBank, setBrandSelected,
  ]);

  const currentDebtManager = useRef<DebtManager | undefined>();
  useEffect(() => {
    eventsTracking.setBank(bank as BankType);
    eventsTracking.setBrand(brand);
    if (currentDebtManager.current === session.debtManager) return;
    if (config.tracking && isLocalStorageAvailable()) {
      const lsBank = localStorage.getItem(config.bankKey) || '';
      const lsBrand = localStorage.getItem(config.brandKey) || '';
      if ([ lsBank, lsBrand ].includes(session.debtManager as string)) return;

      currentDebtManager.current = session.debtManager;
    }
  }, [ brand, bank ]);

  // save user id for use in case of error TODO: in a more "general" place, not inside the portal
  session.setUserId(profileData?.profile?.user.id);
  eventsTracking.setUserId(profileData?.profile?.user.id);

  // negotiator redirect was removed - Deal-Maker integration cleanup

  return (
    <Box className={classes.wrapper}>
      <DebtNegExtraContextProvider>
        <NonPaymentReasonFormContextProvider>
          <NegotiationStateOverrideContextProvider>
            <Box className={`${classes.root} PortalRoot`}>
              <PortalMenu parentUrl={url} parentPath={path} inIdVerification={inIdVerification} />
              <Box className={`${classes.content} PortalRoot__content`} hidden={loading} display={inIdVerification ? 'flex' : 'block'}>
                <Switch>
                  <Route
                    exact
                    path={`${path}`}
                    render={() => {
                      if (loading) return null;
                      if (hasToken && session.identityValidated) {
                        if (hasOutDebts) return <Redirect to={`${url}/deudas`} />;
                        return (
                          <PortalNoOutDebts
                            document={profileData?.profile?.document as string}
                          />
                        );
                      }
                      if (deepLinkToken) return <Redirect to={`${url}/${deepLinkPath}`} />;
                      return <Redirect to={`${path}/${idVerificationPath}`} />;
                    }}
                  />
                  <PrivateRoute
                    to={`${path}?token=undefined&id=undefined`}
                    path={`${path}/${deepLinkPath}`}
                    component={IdentityConfirmationDeepLink}
                  />
                  <PrivateRoute
                    to={url}
                    path={`${path}/deuda/:debtId/:step?`}
                    component={DebtDetails}
                  />
                  <PrivateRoute
                    to={url}
                    path={`${path}/deudas`}
                    component={SelectDebt}
                  />

                  {/* verification over negotiation */}
                  <Route
                    path={`${path}/${idVerificationPath}`}
                    component={IdentificationConfirmation}
                  />
                </Switch>
              </Box>
              {/* loading */}
              <LoaderDialog open={loading} />
            </Box>
            {!inIdVerification && <Footer />}
          </NegotiationStateOverrideContextProvider>
        </NonPaymentReasonFormContextProvider>
      </DebtNegExtraContextProvider>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  wrapper: {
    minHeight: '100vh',
    height: '100vh',
    width: '100vw',
    display: 'flex',
    overflow: 'hidden',
    flexDirection: 'column',
    /*     [theme.breakpoints.down('sm')]: {
      height: 'auto',
    }, */
  },
  root: {
    '&::-webkit-scrollbar': {
      height: 0,
      width: 0,
    },
    overflow: 'scroll',
    display: 'flex',
    backgroundColor: '#ffffff',
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  content: {
    flexGrow: 1,
    overflowY: 'auto',
  },
}));

export default PortalRoot;
